
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabaseWithAuth } from '@/utils/supabaseWrapper';
import { useAuth } from './useAuth';
import { useOrganizationFeatures } from './useOrganizationFeatures';
import { useToast } from '@/hooks/use-toast';
import type { CampaignReport } from '@/types/report';

export const useReports = () => {
  const { user, profile } = useAuth();
  const { organizationData } = useOrganizationFeatures();
  const { toast } = useToast();
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // ✅ Extraire uniquement les valeurs primitives pour éviter les re-renders
  const userRole = organizationData?.userRole;
  const orgId = profile?.organization_id;

  const { data: reports, refetch: refetchReports } = useQuery({
    queryKey: ['reports', user?.id, orgId, userRole],
    queryFn: async (): Promise<CampaignReport[]> => {
      if (!user?.id) {
        console.error('❌ User not authenticated in useReports');
        throw new Error('User not authenticated');
      }

      if (!orgId) {
        console.error('❌ User not associated with an organization');
        throw new Error('User not associated with an organization');
      }

      const { data, error } = await supabaseWithAuth.executeWithRetry(async () => {
        let query = supabaseWithAuth
          .from('campaign_reports')
          .select(`
            *,
            campaigns(candidate_name, candidate_email, target_position, status, selected_evaluation_criteria, organization_id)
          `)
          .order('generated_at', { ascending: false })
          .limit(50); // Limitation pour éviter les requêtes trop lourdes

        // Filtre selon le rôle (aligné avec useCampaignsWithPolling)
        // member: voit uniquement ses rapports
        // admin/owner: voient tous les rapports de l'org via JOIN campaigns
        if (userRole === 'member') {
          query = query.eq('recruiter_id', user.id);
        } else {
          // Pour admin/owner, on filtre via la relation campaigns.organization_id
          // La RLS gère déjà l'accès, mais on ajoute un filtre explicite pour performance
          query = query.eq('campaigns.organization_id', orgId);
        }

        return query;
      }, 'reports.fetch_all');

      if (error) {
        console.error('❌ Error fetching reports:', error);
        throw error;
      }
      
      // Filtrer pour ne garder que les rapports des campagnes terminées
      const completedReports = (data || []).filter((report: any) => 
        report.campaigns?.status === 'Terminé'
      );
      
      
      
      
      return completedReports as unknown as CampaignReport[];
    },
    enabled: !!user?.id && !!orgId,
    // Cache de 15 minutes pour les rapports
    staleTime: 15 * 60 * 1000,
    // Retry en cas d'erreur temporaire
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Déclencher manuellement la génération d'un rapport
  const generateReport = async (campaignId: string) => {
    setGeneratingReport(true);
    try {
      const { data, error } = await supabaseWithAuth.client.functions.invoke('generate-ai-report', {
        body: { campaignId },
      });

      if (error) throw error;

      toast({
        title: "Rapport généré",
        description: "Le rapport de références a été généré avec succès.",
      });

      refetchReports();
      return data;
    } catch (_error) {
      console.error('Erreur génération rapport:', _error);
      toast({
        title: "Erreur",
        description: "Impossible de générer le rapport.",
        variant: "destructive",
      });
      throw _error;
    } finally {
      setGeneratingReport(false);
    }
  };

  // Obtenir un rapport spécifique
  const getReport = async (reportId: string) => {
    if (!reportId) {
      throw new Error('Report ID is required');
    }

    try {
      const { data, error } = await supabaseWithAuth.executeWithRetry(async () => {
        return supabaseWithAuth
          .from('campaign_reports')
          .select(`
            *,
            campaigns(candidate_name, candidate_email, target_position, status, selected_evaluation_criteria)
          `)
          .eq('id', reportId)
          .maybeSingle();
      }, 'reports.get_single');

      if (error) {
        console.error('Error fetching report:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Report not found');
      }

      return data;
    } catch (_error) {
      console.error('Error in getReport:', _error);
      throw _error;
    }
  };

  return {
    reports,
    generatingReport,
    generateReport,
    getReport,
    refetchReports
  };
};
